import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { DiaryRootState } from 'appRoot/rootReducer';
import {BookingStatusNames, FetchingStatus, IBooking, IBookingStatus, servicePaymentType} from "shared-types";
import { IStatusState } from './types';
import StatusConfigService from "external-services/status-config-service";
import DiaryPaymentService from "external-services/diary-payment-service";
import LoginActions from "app/actions/login.actions";
import VenueActions from "app/actions/venue.actions";
import PouchActions from "app/actions/pouch.actions";
import TrackingService from "app/services/tracking/tracking.service";
import {StatusServiceInstance} from "external-services/status-service/index";
import {cloneDeep} from "lodash-es";

const NS = 'statusSlice';

const initialState: IStatusState = {
  fullStatusOptions: [],
  statusOptions: [],
  status: FetchingStatus.idle,
};



// export const loadStatuses = createAsyncThunk(
//   'status/loadStatuses',
//   async (_, { getState, rejectWithValue, dispatch }) => {
//
//     return {
//       statusOptions: StatusConfigService.statusOptions,
//       fullStatusOptions: StatusConfigService.fullStatusOptions,
//     };
//   }
// );

function initStatusService(fullStatusOptions: IBookingStatus[], statusOptions: IBookingStatus[]) {
  // need to make clones of statusOptions and fullStatusOptions so they are mutable when callback is used
  StatusServiceInstance.init(() => ({ statusOptions: cloneDeep(statusOptions), fullStatusOptions: cloneDeep(fullStatusOptions) }));
}

export const loadStatuses = createAsyncThunk(
  'status/loadStatuses',
  async (_, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState() as DiaryRootState;
      const { venueReducer } = state;
      const { fullStatusOptions, statusOptions } = await StatusConfigService.init({
        standByList: Boolean(venueReducer.standByList),
        PouchActions: PouchActions,
        LoginActions: LoginActions,
        VenueActions: VenueActions,
        trackEvent: (...args) => TrackingService.trackEvent(...args),
        statusChangedCallback: (fullStatusOptions, statusOptions) => {
          // when there's an update to statuses in couch, this refreshes the store
          dispatch(actions.setFullStatusOptions(fullStatusOptions));
          dispatch(actions.setStatusOptions(statusOptions));
          initStatusService(fullStatusOptions, statusOptions);
        }
      });

      initStatusService(fullStatusOptions, statusOptions);

      return { fullStatusOptions, statusOptions };
    } catch (e: any) {
      console.log(NS, 'fetchStatues error', e);
      StatusServiceInstance.init(() => ({ statusOptions: [], fullStatusOptions: [] }));

      return rejectWithValue(e.message);
    }
    return initialState;
  }
);


export const getStatusByName = createAsyncThunk(
  'status/getStatusByName',
  async (payload: {
    newStatusName: string,
    oldStatusName: string,
    activeBookingPaymentType: servicePaymentType,
    orgBooking: IBooking
  }, { getState }) => {
    const {
      statusReducer: { fullStatusOptions },
    } = getState() as DiaryRootState;

    let { newStatusName } = payload;
    const {
      activeBookingPaymentType,
      orgBooking
    } = payload

    let newStatus: IBookingStatus;

    if (!newStatusName) {
      const orgPaymentDetails = DiaryPaymentService.getPaymentDetailsByBooking(orgBooking);
      const orgBookingIsFree =
        !orgPaymentDetails || orgPaymentDetails.paymentType === servicePaymentType.noPayment;
      const newBookingHasPayment = activeBookingPaymentType !== servicePaymentType.noPayment;
      if (orgBookingIsFree && newBookingHasPayment) {
        // they just added a payment set the status to payment pending
        newStatusName = BookingStatusNames.PendingPayment;
      }
    }

    if (newStatusName) {
      const status = fullStatusOptions.find(
        (status: IBookingStatus) => status.name.toLowerCase() === newStatusName.toLowerCase()
      )

      if (status) {
        newStatus = status
      }
    }

    return {
      newStatus,
      hasChanged: newStatus && orgBooking.status.name !== newStatus.name,
    }
  }
);

const statusSlice = createSlice({
  name: 'status', // used in action types
  initialState,
  reducers: {
    setStatusOptions: (state: IStatusState, action: PayloadAction<IBookingStatus[]>) => {
      state.statusOptions = action.payload;
    },
    setFullStatusOptions: (state: IStatusState, action: PayloadAction<IBookingStatus[]>) => {
      state.fullStatusOptions = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadStatuses.pending, (state, action) => {
        state.status = FetchingStatus.loading;
      })
      .addCase(loadStatuses.fulfilled, (state: IStatusState, action) => {
        state.fullStatusOptions = action.payload.fullStatusOptions;
        console.log(NS, 'loadStatuses.fulfilled', action.payload)
        state.statusOptions = action.payload.statusOptions;
        state.status = FetchingStatus.succeeded;
      });
  },
});

// Extract the action creators object and the reducer
const { actions, reducer } = statusSlice;
// Extract and export each action creator by name
export { actions as statusActions };
// Export the reducer, either as a default or named export
export default reducer;
